// SOA Types

// Control represents a security control in a compliance framework
export interface Control {
  id: string;
  controlID: string;  // Human-readable ID (e.g., AC-1)
  name: string;
  description: string;
  framework: string;  // Group of the framework this control belongs to
  frameworkRaw: string;  // Name of the framework this control belongs to
  frameworkId: string;  // Name of the framework this control belongs to
  applicable: boolean; // Is this control applicable to the organization
  status: 'Fully implemented' | 'Partially implemented' | 'Not implemented' | 'Not applicable' | 'Unknown';
  justification: string; // Why this control is applicable or not
  comments?: string;  // Additional notes
  owner?: string;     // Who is responsible for this control
  isRFFR?: boolean;   // True if this is an RFFR control (ISM control with os or s set to yes/true)
}

// ControlUpdateRequest represents the data used to update a control
export interface ControlUpdateRequest {
  applicable: boolean;
  status: string;
  justification?: string;
  comments?: string;
  owner?: string;
}

// ImplementationStatus represents statistics about control implementation statuses
export interface ImplementationStatus {
  implemented: number;
  notImplemented: number;
  partiallyImplemented: number;
  notApplicable: number;
  unknown: number;
}

// Framework represents a compliance framework (e.g., NIST 800-53, ISO 27001)
export interface Framework {
  id: string;
  name: string;
  description: string;
  controlCount: number;  // Number of controls in this framework
}

// FrameworkStats represents statistics for controls within a framework
export interface FrameworkStats {
  id: string;       // Framework ID
  name: string;     // Framework name
  stats: ImplementationStatus; // Using the ImplementationStatus interface for stats
}

// Version information for the SOA document
export interface VersionInfo {
  number: string;
  lastUpdated: Date;
}

// Version history entry for tracking changes
export interface VersionHistoryEntry {
  versionKey: string;
  versionNumber: string;
  dateUpdated: Date;
  updatedBy: string;
  updateComments: string;
  updatesMade?: Record<string, any>;
}

// Complete SOA data structure
export interface SOAData {
  controls: Control[];
  frameworks: Framework[];
  stats: FrameworkStats[];
  version: VersionInfo;
}

// Empty object as fallback for when no data is available
export const emptySOAData: SOAData = {
  frameworks: [],
  controls: [],
  stats: [],
  version: {
    number: '1.0.0',
    lastUpdated: new Date()
  }
};